import React from 'react';

/**
 * RichDescription
 * ----------------
 * Renders a long product description in a beautifully readable layout by
 * auto-detecting garment-piece keywords (SHIRT, DUPATTA, GHARARA, etc.)
 * the way couture houses traditionally describe ensembles.
 *
 * Usage:
 *   <RichDescription text={product.description} />
 *
 * Behaviour:
 *   1. If the description contains recognised UPPERCASE keywords like
 *      "SHIRT", "DUPATTA", "GHARARA" at the start of a sentence, it splits
 *      the text into labelled sections.
 *   2. Otherwise, if the description has explicit blank-line breaks, each
 *      paragraph is rendered separately.
 *   3. Otherwise, it falls back to a single paragraph.
 *
 * The admin needs no special syntax — they simply write the description
 * naturally and it formats itself.
 */

// Recognised garment-piece keywords (extend freely; admins benefit immediately).
export const SECTION_KEYWORDS = [
  // Tops & dresses
  'SHIRT', 'KURTA', 'KURTI', 'BLOUSE', 'CHOLI', 'TOP', 'JACKET', 'KAMEEZ',
  'ANARKALI', 'GOWN', 'DRESS', 'KAFTAN', 'ABAYA', 'JUMPSUIT', 'CAPE', 'CORSET',
  // Bottoms
  'GHARARA', 'SHARARA', 'LEHENGA', 'SKIRT', 'PANTS', 'PALAZZO', 'SHALWAR',
  'PATIALA', 'DHOTI', 'CHURIDAR', 'TROUSER', 'TROUSERS',
  // Layering / accessories
  'DUPATTA', 'SAREE', 'PALLU', 'BORDER', 'YOKE', 'DAMAN', 'KALI', 'GOTH',
  'INNER', 'CAN-CAN', 'BELT', 'WAISTBAND', 'NECKLINE', 'SLEEVES',
  // General
  'ENSEMBLE', 'OUTFIT', 'SUIT',
];

/**
 * Parse a description into [{ heading, body }] sections.
 * Headings are kept null when the segment has no matching keyword (e.g. lead text).
 */
export function parseDescription(text) {
  const cleaned = (text || '').trim();
  if (!cleaned) return [];

  // A section heading is one of the recognised keywords appearing as a
  // standalone uppercase word, preceded by start-of-text / whitespace /
  // punctuation and followed by whitespace + the start of a sentence.
  const pattern = new RegExp(
    `(?:^|[\\s.,;:\u2014\u2013-])((?:${SECTION_KEYWORDS.join('|')}))(?=\\s)`,
    'g',
  );

  const matches = [];
  let m;
  while ((m = pattern.exec(cleaned)) !== null) {
    const keywordStart = m.index + (m[0].length - m[1].length);
    matches.push({ start: keywordStart, end: keywordStart + m[1].length, word: m[1] });
    // Step the lastIndex back by 0 so overlapping zero-length lookaheads behave.
    if (m.index === pattern.lastIndex) pattern.lastIndex += 1;
  }

  // No structured markers -> fall back to paragraph split or single block.
  if (matches.length === 0) {
    const paras = cleaned.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
    return paras.map((p) => ({ heading: null, body: p }));
  }

  const sections = [];
  if (matches[0].start > 0) {
    const lead = cleaned.slice(0, matches[0].start).trim();
    if (lead) sections.push({ heading: null, body: lead });
  }
  for (let i = 0; i < matches.length; i += 1) {
    const next = matches[i + 1];
    const bodyStart = matches[i].end;
    const bodyEnd = next ? next.start : cleaned.length;
    const body = cleaned.slice(bodyStart, bodyEnd).replace(/^[\s.,;:\u2014\u2013-]+/, '').trim();
    sections.push({ heading: matches[i].word.toUpperCase(), body });
  }
  return sections.filter((s) => s.body || s.heading);
}

const RichDescription = ({ text, className = '', testId = 'rich-description' }) => {
  const sections = parseDescription(text);
  if (!sections.length) return null;

  return (
    <div className={`rich-description ${className}`} data-testid={testId}>
      {sections.map((s, i) => (
        <div key={i} className="rich-description__section">
          {s.heading && (
            <h4 className="rich-description__heading">{s.heading}</h4>
          )}
          <p className="rich-description__body">{s.body}</p>
        </div>
      ))}
    </div>
  );
};

// One-time scoped styles
const STYLE_ID = 'rich-description-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const css = document.createElement('style');
  css.id = STYLE_ID;
  css.textContent = `
    .rich-description {
      display: flex;
      flex-direction: column;
      gap: 24px;
      margin: 24px 0 32px;
    }
    .rich-description__section {
      display: grid;
      grid-template-columns: 110px 1fr;
      gap: 28px;
      align-items: start;
    }
    .rich-description__section:has(.rich-description__heading) {
      padding-top: 4px;
    }
    .rich-description__heading {
      font-family: 'Inter', sans-serif;
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--accent-gold);
      margin: 6px 0 0;
      line-height: 1.4;
      position: relative;
    }
    .rich-description__heading::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -10px;
      width: 28px;
      height: 1px;
      background: rgba(201, 169, 97, 0.45);
    }
    .rich-description__body {
      margin: 0;
      font-family: 'Inter', sans-serif;
      font-size: 15px;
      line-height: 1.85;
      color: var(--text-primary);
      letter-spacing: 0.005em;
    }
    /* When a section has no heading, span both columns (lead/intro paragraph). */
    .rich-description__section:not(:has(.rich-description__heading)) {
      grid-template-columns: 1fr;
    }
    .rich-description__section:not(:has(.rich-description__heading)) .rich-description__body {
      font-size: 16px;
      color: var(--text-primary);
    }
    @media (max-width: 640px) {
      .rich-description { gap: 22px; }
      .rich-description__section {
        grid-template-columns: 1fr;
        gap: 10px;
      }
      .rich-description__heading::after { bottom: -6px; }
      .rich-description__body { font-size: 14px; }
    }
  `;
  document.head.appendChild(css);
}

export default RichDescription;
